<template>
    <div class="flex flex-col h-full w-full gap-2">
        <div class="flex flex-col h-0 grow w-full">
            <DxDataGrid
                ref="dxGridRef"
                :allow-column-reordering="true"
                :allow-column-resizing="true"
                :autoExpandAll="false"
                :column-auto-width="true"
                :data-source="dataSource"
                :enable-group-panel="true"
                :filter-panel="{ visible: vFilterPanelShow }"
                :filter-row="vFilterRow.visible === undefined ? { ...vFilterRow, visible: true } : vFilterRow"
                :focused-row-enabled="true"
                :focused-row-key="focusedRowKey"
                :keyExpr="meta?.keyExpr"
                :remote-operations="true"
                :show-borders="true"
                :show-column-lines="true"
                :state-storing="{ enabled: true, type: 'custom', customSave: saveState, savingTimeout: 500 }"
                :word-wrap-enabled="meta?.wordWrapEnabled ?? true"
                column-resizing-mode="widget"
                height="100%"
                width="100%"
                @focusedRowChanging="onFocusedRowChanging"
                @rowDblClick="onRowDblClick"
                @context-menu-preparing="onContextMenu"
                @editor-preparing="editorPreparing"
                @option-changed="onOptionChangedFunc"
                @selection-changed="selectionChanged"
            >
                <dx-toolbar :height="'64px'" :visible="toolbarShow">
                    <dx-item
                        v-for="(item, index) in toolbarConfigAfter"
                        :key="index"
                        :name="item.component.name"
                        location="after"
                    >
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            :disabled="refs[item.component.disabledRefName]"
                            :hint="item.component.hint"
                            class="ai-button-base"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                        <dx-text-box
                            v-if="item.widget === 'dx-text-box'"
                            v-model:value="refs[item.component.valueRefName.name]"
                            :label="
                                refs[item.component.valueLabelName]
                                    ? refs[item.component.valueLabelName]
                                    : item.component.valueLabelName
                            "
                            :width="item.component.width"
                            class="ai-toolbar-texteditor"
                            @enter-key="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'enter-key';
                                    eventController(event);
                                }
                            "
                            @key-up="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'key-up';
                                    eventController(event);
                                }
                            "
                        >
                            <DxTextBoxButton
                                v-for="(inputButton, indexButton) in item.component.inputButtons"
                                :key="indexButton"
                                :name="inputButton.name"
                                :options="inputButton.options"
                                location="after"
                            />
                        </dx-text-box>
                        <DxDropDownButton
                            v-if="item.widget === 'dx-drop-down-button'"
                            :dropDownOptions="item.component.dropDownOptions"
                            :icon="item.component.htmlElement"
                            :items="item.component.items"
                            :split-button="true"
                            hint="Экспортировать"
                            @initialized="onDxDropDownButtonInitialized"
                        >
                            <dx-item v-for="(item, index) in item.component.items" :key="index">
                                <div
                                    :class="{
                                        'opacity-25': refs[item.component.disabledRefName],
                                        'ai-dx-drop-down-button-item dx-template-wrapper dx-item-content dx-list-item-content': true,
                                    }"
                                    @click="
                                        event => {
                                            event['item'] = item;
                                            event['eventName'] = 'click';
                                            eventController(event);
                                        }
                                    "
                                >
                                    <component
                                        :is="item.component.htmlElement"
                                        :disabled="refs[item.component.disabledRefName]"
                                        :isChecked="vIsCurrentSettingsUser"
                                        class="ai-dx-drop-down-button-item-svg"
                                    />
                                    <div>
                                        {{
                                            typeof item.component.text === 'function'
                                                ? item.component.text()
                                                : item.component.text
                                        }}
                                    </div>
                                </div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>
                    <dx-item v-for="(item, index) in toolbarConfigBefore" :key="index" location="before">
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            :disabled="refs[item.component.disabledRefName]"
                            :hint="item.component.hint"
                            class="ai-button-base"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                    </dx-item>
                    <dx-item :visible="groupPanelVisible" location="before" name="groupPanel" />
                </dx-toolbar>
                <dx-editing
                    :allow-adding="false"
                    :allow-deleting="false"
                    :allow-updating="false"
                    :use-icons="true"
                    mode="batch"
                />
                <DxSearchPanel :visible="true" placeholder="Фильтр..." />
                <DxSelection :allowSelectAll="false" :mode="vSelectionMode" />
                <DxGroupPanel v-if="vIsGrouping" :visible="groupPanelVisible" />
                <DxGrouping v-if="vIsGrouping" :auto-expand-all="false" :contextMenuEnabled="true" />
                <DxFilterBuilder :group-operations="['and', 'notAnd']" />
                <DxPaging :enabled="true" :page-size="100" />
                <DxSorting mode="single" />
                <DxScrolling mode="virtual" />
                <DxExport :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
                <DxRemoteOperations :group-paging="true" />
                <DxColumnChooser :enabled="true" :height="500" :width="300" mode="select">
                    <DxColumnChooserSearch :enabled="true" />
                    <DxColumnChooserSelection />
                </DxColumnChooser>
                <DxSummary>
                    <DxGroupItem column="Id" summary-type="count" />
                </DxSummary>
                <template v-for="template in cellTemplates" :key="template.name" #[template.name]="{ data: ctx }">
                    <component :is="template.component" :data="ctx" />
                </template>
                <dx-column
                    v-for="(item, index) in meta.columns"
                    :key="index"
                    :alignment="item.alignment || 'left'"
                    :allowSorting="true"
                    :caption="item.text"
                    :data-field="item.field"
                    :editor-options="{
                        maxLength: item.maxLength,
                        showClearButton: isDate(item),
                    }"
                    :visible="item.visible"
                    :width="item.width"
                    headerCellTemplate="headerTemplate"
                />
                <template #headerTemplate="{ data: headerInfo }">
                    <div class="ai-column-header-template">
                        {{ headerInfo.column.caption }}
                    </div>
                </template>
            </DxDataGrid>
        </div>
        <ai-grid-virtual-footer
            :current-value="currentValue"
            :selected-count="selectedCount"
            :show-selected="totalCountSelectedShow"
            :total-count="totalCount"
        ></ai-grid-virtual-footer>
    </div>
</template>

<script>
export default {
    name: 'ai-grid-virtual-base',
};

/* Чтобы ограничить количество символов, которые можно добавить в поле ввода, необходимо в мету колонки прописать настройку 'maxLength: 250' (например, 250 символов)*/

/*
Чтобы слова в заголовках столбцов НЕ переносились необходимо добавить в мету этой таблицы настройку 'wordWrapEnabled: false,'
*/
</script>
<script setup>
import {
    DxDataGrid,
    DxToolbar,
    DxItem,
    DxEditing,
    DxSearchPanel,
    DxExport,
    DxSelection,
    DxGroupPanel,
    DxGrouping,
    DxFilterBuilder,
    DxPaging,
    DxSorting,
    DxScrolling,
    DxRemoteOperations,
    DxColumn,
    DxColumnChooser,
    DxColumnChooserSearch,
    DxColumnChooserSelection,
    DxSummary,
    DxGroupItem,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { DxTextBox, DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import AiGridVirtualFooter from '@/components/ai-grid-virtual/ai-grid-virtual-footer.vue';
import { computed, defineProps, onBeforeMount, onMounted, ref } from 'vue';
import DataSource from 'devextreme/data/data_source';
import { gridCardStore } from '@/components/ai-grid-virtual/grid-virtual-store';
import { useRouter } from 'vue-router';
import { dxGridUserSettings } from '@/utils/dx/dx-data-grid-utile';
import deepEqual from '@/utils/deepEqual';

let dataSource = ref(null);
const gridColumns = ref([]);
const cellTemplates = [];
// const editCellTemplates = [];
const gridInstance = ref(null);
const dxGridRef = ref(null);
const totalCount = ref(0);
const groupPanelVisible = ref(false);
const isDeleteButtonDisabled = ref(true);

const vIsCurrentSettingsUser = ref(true);
const selectedCount = ref(0);
const refs = ref({
    isSaveSettingsButtonDisabled: true,
    isChoiceButtonDisabled: true,
    isSaveButtonDisabled: true,
    isDeleteButtonDisabled: true,
    isCopyButtonDisabled: true,
    isEditButtonDisabled: true,
});
const flagCanSaveColumnsWidth = ref(false);
const focusedRowKey = ref(null);
const getRefValue = name => {
    return refs.value[name];
};
const setRefValue = (name, value) => {
    refs.value[name] = value;
};
const emits = defineEmits([
    'userSettingsSave',
    'userSettingsChanged',
    'getHiddenColumns',
    'focusedRowChanging',
    'selectionChanged',
    'toolbarButtonClick',
    'toolbarTextboxEvent',
    'gridColumnsWidthChanged',
    'gridVirtualBaseMounted',
    'resetUserSettings',
]);
let toolbarConfigBefore = [];
const toolbarConfigInitBefore = [
    {
        widget: 'dx-button',
        component: {
            name: 'choice',
            disabledRefName: 'isChoiceButtonDisabled',
            hint: 'Выбрать и закрыть',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-choice-base',
        },
    },
];

let toolbarConfigAfter = [];
const toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'copy',
            disabledRefName: 'isCopyButtonDisabled',
            hint: 'Создать копию сфокусированной строки',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-copy-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'edit',
            disabledRefName: 'isEditButtonDisabled',
            hint: 'Редактировать',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-edit-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'add',
            hint: 'Добавить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-add-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'delete',
            disabledRefName: 'isDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'save',
            disabledRefName: 'isSaveButtonDisabled',
            hint: 'Сохранить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-save-base',
        },
    },
    {
        widget: 'searchPanel',
        component: {
            name: 'searchPanel',
        },
    },
    {
        widget: 'dx-drop-down-button',
        component: {
            name: 'export',
            htmlElement: `<svg width="32" height="32" viewBox="0 0 76 74" fill="none" xmlns="http://www.w3.org/2000/svg">
<g clip-path="url(#clip0_1695_4138)">
<path fill-rule="evenodd" clip-rule="evenodd" d="M5 0C2.23858 0 0 2.23858 0 5V69C0 71.7614 2.23858 74 5 74H57C59.7614 74 62 71.7614 62 69V21H44C42.3431 21 41 19.6569 41 18V0H5Z" fill="#286EB6"/>
<path d="M45 1L53 9L61 17H48C46.3431 17 45 15.6569 45 14V1Z" fill="#286EB6"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879ZM38.2068 51.2919L52.8038 51.2879ZM52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" fill="white"/>
<path d="M38.2068 51.2919L38.2073 51.2951H35.8929C34.0764 51.2951 32 49.9902 32 47.6987V47.6962L32.0092 40.4159C32.0092 40.4154 32.0092 40.4149 32.0092 40.4145V40.4134L34.0092 40.4159L38.2068 51.2919ZM38.2068 51.2919L52.8038 51.2879M52.8038 51.2879V53.4029C52.8038 55.0994 53.9901 56.2437 55.1601 56.7072C56.3556 57.1808 57.8713 57.1203 59.1285 56.2118L72.4615 46.9241L72.4634 46.9228C73.4441 46.238 74.0333 45.1581 73.9986 43.9711C73.9647 42.812 73.3441 41.7944 72.4131 41.1548L59.0607 31.7374L59.0595 31.7365C57.8234 30.866 56.3249 30.8338 55.1618 31.2933C54.0082 31.7491 52.808 32.8766 52.8073 34.5936C52.8073 34.5941 52.8073 34.5946 52.8073 34.5951L52.8057 36.8272L52.8038 51.2879Z" stroke="#286EB6" stroke-width="4"/>
<path d="M32 41C32 39.8954 32.8954 39 34 39H55V49H34C32.8954 49 32 48.1046 32 47V41Z" fill="white"/>
</g>
<defs>
<clipPath id="clip0_1695_4138">
<rect width="76" height="74" fill="white"/>
</clipPath>
</defs>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-all-base',
                        name: 'export-pdf-all',
                        text: 'Экспортировать все в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-pdf-selected-base',
                        name: 'export-pdf-selected',
                        text: 'Экспортировать выбранные строки в pdf',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-all-base',
                        name: 'export-excel-all',
                        text: 'Экспортировать всё в EXCEL',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        htmlElement: 'ai-export-excel-selected-base',
                        name: 'export-excel-selected',
                        text: 'Экспортировать выбранные строки в EXCEL',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-drop-down-button',
        component: {
            name: 'settings',
            htmlElement: `<svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z" fill="#286EB6"/>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'group-area',
                        text: () => {
                            return !groupPanelVisible.value
                                ? 'Показать область группировки'
                                : `${String.fromCharCode(10004)}  Показать область группировки`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-auto-settings-save',
                        text: 'Автоматически сохранять настройки',
                        isChecked: vIsCurrentSettingsUser.value,
                        htmlElement: 'ai-checkbox',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-user-settings',
                        text: 'Сохранить настройки',
                        disabledRefName: 'isSaveSettingsButtonDisabled',
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'clear-user-settings',
                        text: 'Сброс настроек пользователя',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'show-column-chooser',
            hint: 'Выбор столбцов',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-show-column-chooser-base',
        },
    },
];

const router = useRouter();

const props = defineProps({
    meta: {
        type: Object,
        default: () => {
            return {
                columns: [],
                editViewRoutName: {
                    type: String,
                    default: () => {
                        return undefined;
                    },
                },
                createViewRoutName: { type: String, default: () => undefined },
            };
        },
    },
    selectionMode: {
        type: String,
    },
    toolbarShow: {
        type: Boolean,
        default: true,
    },
    totalCountSelectedShow: {
        type: Boolean,
        default: () => true,
    },
    isGrouping: {
        type: Boolean,
        default: () => undefined,
    },
    dropDownSettings: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    columnChooserButton: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    searchButton: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    saveButton: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    filterRow: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    filterPanelShow: {
        type: Boolean,
        default: () => {
            return undefined;
        },
    },
    autoLoadData: {
        type: Boolean,
        default: true,
    },
    editViewRoutName: {
        type: String,
        default: () => {
            return undefined;
        },
    },
    copyButton: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    deleteButton: {
        type: Object,
        default: () => {
            return undefined;
        },
    },
    customContextMenu: {
        type: Array,
        default: () => [],
    },
    toolbarUserItemsAfter: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => [],
    },
    currentValue: {
        type: String,
    },
    loadUserSettingsCallback: {
        type: Function,
        default: () => {
            return { columns: [] };
        },
    },
    setContext: {
        type: Function,
        default: null,
    },
});

onBeforeMount(async () => {
    // toolbar
    const toRemoveToolbarItemsNames = [
        ...new Set([...props.toRemoveToolbarItemsNames, ...(props.meta?.toRemoveToolbarItemsNames || [])]),
    ];
    for (const item of toolbarConfigInitAfter) {
        if (!toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }
    }
    for (const item of toolbarConfigInitBefore) {
        if (!toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigBefore.push({ ...item });
        }
    }
    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };
    const toolbarUserItemsAfter = [...props.toolbarUserItemsAfter].sort(compare);

    for (const item of toolbarUserItemsAfter) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }
    // load data
    const vAutoLoadData = computed(() => {
        if (props.meta?.autoLoadData !== undefined) {
            return props.meta?.autoLoadData;
        }
        if (props.autoLoadData !== undefined) {
            return props.autoLoadData;
        }
        return true;
    });
    if (vAutoLoadData.value) {
        await loadData();
    }
});
onMounted(async () => {
    gridInstance.value = dxGridRef.value?.instance;
    if (props.meta?.columns) gridColumns.value = processColumns(props.meta?.columns, null);
    emits('gridVirtualBaseMounted');
});

const isDate = (column) => column.dataType === 'date' || column.dataType === 'datetime';

const eventController = async e => {
    // if (!e.item.component.emits.find(item=>item.name === e.eventName)) {
    //   return;
    // }
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
    if (e.item.widget === 'dx-text-box') {
        if (e.eventName === 'enter-key' || e.eventName === 'key-up') {
            await toolbarTextboxEvent(e);
        }
    }
    if (e.item.widget === 'dx-drop-down-item') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
};

const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'choice':
            await onChoiceClick(e);
            break;
        case 'refresh':
            await onRefreshClick(e);
            break;
        case 'edit':
            await onEditClick(e);
            break;
        case 'add':
            await onAddClick(e);
            break;
        case 'export-excel-all':
            onExcelExportAll(e);
            break;
        case 'export-excel-selected':
            onExcelExportSelected(e);
            break;
        case 'export-pdf-all':
            onPdfExportAll(e);
            break;
        case 'export-pdf-selected':
            onPdfExportSelected(e);
            break;
        case 'group-area':
            onGroupAreaClick(e);
            break;
        case 'apply-auto-settings-save':
            applyAutoSettingsSave(e);
            break;
        case 'apply-user-settings':
            applyUserSettings(e);
            break;
        case 'clear-user-settings':
            clearUserSettings(e);
            break;
        case 'show-column-chooser':
            dxGridRef.value?.instance.showColumnChooser();
            break;
        default:
            break;
    }
    emits('toolbarButtonClick', e);
};

const onChoiceClick = e => {
    const selectedRowKeys = gridInstance.value.getSelectedRowKeys();
    const selectedRows = gridInstance.value.getSelectedRowsData();
    e['data'] = { selectedRowKeys, selectedRows, focusedRowKey: focusedRowKey.value };
};

const toolbarTextboxEvent = async e => {
    switch (e.eventName) {
        case 'enter-key':
            switch (e.item.component.name) {
                case 'find':
                    await onEnterKeyFilter(e);
                    break;
                default:
                    break;
            }
            break;
        case 'key-up':
            switch (e.item.component.name) {
                case 'find':
                    await onFilterKeyup(e);
                    break;
                default:
                    break;
            }
            break;
        default:
            break;
    }
    emits('toolbarTextboxEvent', e);
};

const onDxDropDownButtonInitialized = e => {
    // const btn = window.document.getElementById('AA513DA2-AD16-49A9-949A-FE2ECE221D68');
    // let div = window.document.createElement('ai-add-base');
    // btn.append(div);
    // console.log('onDxDropDownButtonInitialized', e);
};

const applyAutoSettingsSave = () => {
    refs.value['isSaveSettingsButtonDisabled'] = !vIsCurrentSettingsUser.value;
    vIsCurrentSettingsUser.value = !vIsCurrentSettingsUser.value;
};

function onPdfExportAll(e) {
    console.log('onPdfExportAll', e);
}

function onPdfExportSelected(e) {
    console.log('onPdfExportSelected', e);
}

function onExcelExportAll(e) {
    console.log('onExcelExportAll', e);
}

function onExcelExportSelected(e) {
    console.log('onExcelExportSelected', e);
}

const onEnterKeyFilter = e => {
    console.log('onEnterKeyFilter($event): ', e);
};

const clearUserSettings = e => {
    emits('resetUserSettings');
};
const onFilterKeyup = async e => {
    console.log('onFilter1Keyup($event): ', e.event.currentTarget.value);
};
const onRefreshClick = async () => {
    await gridInstance.value.refresh();
};

const loadData = async () => {
    // initialMetaData
    if (dataSource.value === null) {
        dataSource.value = new DataSource(gridCardStore(props.meta));
        dataSource.value.on('changed', e => {
            totalCount.value = dataSource.value.totalCount();
        });
    }
};

const vSelectionMode = computed(() => {
    if (props.selectionMode !== undefined) {
        return props.selectionMode;
    }
    if (props.meta?.selectionMode !== undefined) {
        return props.meta?.selectionMode;
    }
    return 'multiple';
});
const vIsGrouping = computed(() => {
    if (props.isGrouping !== undefined) {
        return props.isGrouping;
    }
    if (props.meta?.isGrouping !== undefined) {
        return props.meta?.isGrouping;
    }
    return true;
});

const vFilterRow = computed(() => {
    if (props.filterRow !== undefined) {
        return props.filterRow;
    }
    if (props.meta?.FilterRow !== undefined) {
        return props.meta?.FilterRow;
    }
    return {
        visible: true,
    };
});
const vFilterPanelShow = computed(() => {
    if (props.filterPanelShow !== undefined) {
        return props.filterPanelShow;
    }
    if (props.meta?.FilterPanelShow !== undefined) {
        return props.meta?.FilterPanelShow;
    }
    return true;
});

const vEditViewRoutName = computed(() => {
    if (props.editViewRoutName !== undefined) {
        return props.editViewRoutName;
    }
    if (props.meta?.editViewRoutName !== undefined) {
        return props.meta?.editViewRoutName;
    }
    return undefined;
});
const vCreateViewRoutName = computed(() => {
    if (props.meta?.createViewRoutName !== undefined) {
        return props.meta?.createViewRoutName;
    }
    return undefined;
});

const editorPreparing = async e => {
    e.updateValueTimeout = 2000;
    if (e.parentType === 'searchPanel') {
        e.editorOptions.onValueChanged = function (arg) {
            if (this.searchTimeout) {
                clearTimeout(this.searchTimeout);
            }
            this.searchTimeout = setTimeout(() => {
                if (arg.value.length == 0 || arg.value.length > 2) {
                    e.component.searchByText(arg.value);
                }
            }, 2000);
        };
    }
};

// const filterOperations = {
//     string: ['contains', 'notcontains', 'startswith', 'endswith', '=', '<>'],
//     numeric: ['=', '<>', '<', '>', '<=', '>='],
//     number: ['=', '<>', '<', '>', '<=', '>='],
//     date: ['=', 'between'],
// };
const processColumns = async (columns, parentId) => {
    flagCanSaveColumnsWidth.value = false;
    // let userSettings = await props.loadUserSettingsCallback();
    // const getSettingsColumnWidth = columnName => {
    //     const column = userSettings.columns.find(item => item.name === columnName);
    //     if (column) {
    //         return column.width;
    //     }
    //     return undefined;
    // };
    // const resultArr = [];
    // for (const column of columns) {
    //     const obj = {
    //         caption: column.text,
    //         visible: column.visible !== false,
    //     };
    //     obj.dataField = column.field ? column.field : '';
    //     obj.dataType = column.data_type ? column.data_type : '';
    //     obj.filterOperations = column.data_type ? filterOperations[column.data_type] : '';
    //     obj.width = getSettingsColumnWidth(column.name ? column.name : column.field);
    //     if (!obj.width) {
    //         obj.width = column.width ? column.width : 100;
    //     }
    //     obj.groupIndex = column.group_index;
    //     if (column.format) obj.format = column.format;
    //     if (column.cellTemplate) {
    //         const name = `cellTemplate-${column.field}`;
    //         const template = { name, component: column.cellTemplate };
    //         cellTemplates.push(template);
    //         obj.cellTemplate = name;
    //     }
    //     if (column.editCellTemplate) {
    //         const name = `editCellTemplate-${column.field}`;
    //         const template = { name, component: column.editCellTemplate };
    //         editCellTemplates.push(template);
    //         obj.editCellTemplate = name;
    //     }
    //     if (column.editorOptions) obj.editorOptions = column.editorOptions;
    //     if (column.sortingMethod) obj.sortingMethod = column.sortingMethod;
    //     if (column.alignment) obj.alignment = column.alignment;
    //     if (column.fixed) obj.fixed = column.fixed;
    //     if (column.cssClass) obj.cssClass = column.cssClass;
    //     resultArr.push(obj);
    // }
    flagCanSaveColumnsWidth.value = true;
    // return resultArr;
};

const userSettingsData = ref();
const columnsChanging = ref(false);

const saveState = state => {
    const { columns } = state;
    const newUserSettings = { columns };
    const isSettingsHasChanged = !deepEqual(userSettingsData.value, newUserSettings);
    userSettingsData.value = newUserSettings;
    if (columnsChanging.value && isSettingsHasChanged) {
        emits('userSettingsChanged', userSettingsData.value, vIsCurrentSettingsUser.value);
    }
};

const onEditClick = () => {
    const id = gridInstance.value.option('focusedRowKey');
    if (id && vEditViewRoutName.value) {
        router.push({
            name: vEditViewRoutName.value,
            params: { id },
        });
    }
};
const onAddClick = () => {
    if (vCreateViewRoutName.value) {
        router.push({
            name: vCreateViewRoutName.value,
        });
    }
};
const onGroupAreaClick = () => {
    groupPanelVisible.value = !groupPanelVisible.value;
};

// const applyDefaultSettings = () => {
//     vIsCurrentSettingsUser.value = false;
// };

const applyUserSettings = () => {
    emits('userSettingsSave');
    vIsCurrentSettingsUser.value = true;
};

const onContextMenu = event => {
    if (event.target !== 'content') return;
    if (event.row) {
        event.items = [];
        if (vEditViewRoutName.value) {
            event.items.push({
                name: 'gotoCard',
                text: 'Посмотреть карточку',
                visible: true,
                onItemClick() {
                    const id = event.row.data[props.meta.keyExpr];
                    router.push({ name: vEditViewRoutName.value, params: { id } });
                },
            });
        }
        const context = props.customContextMenu.map(item => {
            return { ...item, rowData: event.row };
        });
        event.items.push(...context);
    }
};

const onOptionChangedFunc = e => {
    //console.log('onOptionChanged', e.fullName);
    if (e.fullName.includes('visible')) {
        emits(
            'getHiddenColumns',
            e.component.option('columns').filter(el => !el.visible)
        );
    }
    //columns[2].width
    if (e.name === 'columns') {
        columnsChanging.value = true;
        if (e.fullName.startsWith('columns[') && e.fullName.endsWith('].width')) {
            if (flagCanSaveColumnsWidth.value) {
                emits('gridColumnsWidthChanged');
            }
        }
    }
};

const getUserSettings = () => {
    return dxGridUserSettings(dxGridRef.value?.instance);
};
const setUserSettings = settings => {
    dxGridRef.value?.instance.state(settings);
    if (props.meta?.columns) gridColumns.value = processColumns(props.meta?.columns, null);
};

const onFocusedRowChanging = e => {
    refs.value['isChoiceButtonDisabled'] = false;
    refs.value['isCopyButtonDisabled'] = false;
    refs.value['isEditButtonDisabled'] = false;
    emits('focusedRowChanging', e);
    if (e.prevRowIndex !== e.newRowIndex) {
        if (e.rowElement[0]?.className?.includes('group-row')) {
            e.cancel = true;
            setTimeout(() => {
                e.component.option('focusedRowIndex', -1);
            });
        }
    }
};
const selectionChanged = event => {
    refs.value['isChoiceButtonDisabled'] = false;
    refs.value['isDeleteButtonDisabled'] = event?.selectedRowKeys?.length === 0;
    emits('selectionChanged', event);
    selectedCount.value = event.selectedRowKeys.length;
    event.selectedRowKeys.length > 0 ? (isDeleteButtonDisabled.value = false) : (isDeleteButtonDisabled.value = true);
};

const onRowDblClick = e => {
    if (props.setContext) {
        props.setContext(e.data);
    }
};

defineExpose({
    gridInstance,
    loadData,
    getRefValue,
    setRefValue,
    getUserSettings,
    setUserSettings,
});
</script>
<style scoped>
.ai-column-header-template {
    display: flex;
    width: 100%;
}

:deep(.dx-clear-button-area) {
    margin-left: 8px !important; /* Внешний отступ от поля ввода */
}
</style>
